/**
 * Notification Service
 * Minimal service for managing browser notification permissions and test notifications
 * Note: Actual hydration reminders are handled by OneSignal (see oneSignalService.ts)
 */

type PermissionChangeListener = (status: NotificationPermission) => void;

class NotificationService {
  private permissionChangeListeners: Set<PermissionChangeListener> = new Set();
  private permissionCheckInterval: number | null = null;

  constructor() {
    // Start monitoring permission changes
    this.startPermissionMonitoring();
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    try {
      const permission = await Notification.requestPermission();
      this.notifyPermissionChange(permission);
      return permission === 'granted';
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }

  /**
   * Show a test notification
   */
  showTestNotification(): boolean {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    if (Notification.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return false;
    }

    try {
      const notification = new Notification('SWYG App Test Notification', {
        body: 'This is a test notification. Your hydration reminders will look like this!',
        icon: '/swyg-push-icon.png',
        badge: '/swyg-push-badge.png',
        tag: 'test-notification',
        requireInteraction: false,
        silent: false,
      });

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return true;
    } catch (error) {
      console.error('Failed to show test notification:', error);
      return false;
    }
  }

  /**
   * Add a listener for permission changes
   */
  addPermissionChangeListener(listener: PermissionChangeListener): void {
    this.permissionChangeListeners.add(listener);
  }

  /**
   * Remove a permission change listener
   */
  removePermissionChangeListener(listener: PermissionChangeListener): void {
    this.permissionChangeListeners.delete(listener);
  }

  /**
   * Notify all listeners of permission change
   */
  private notifyPermissionChange(status: NotificationPermission): void {
    this.permissionChangeListeners.forEach((listener) => {
      try {
        listener(status);
      } catch (error) {
        console.error('Error in permission change listener:', error);
      }
    });
  }

  /**
   * Start monitoring permission changes
   * Note: There's no native event for permission changes, so we poll
   */
  private startPermissionMonitoring(): void {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return;
    }

    let lastPermission = this.getPermissionStatus();

    this.permissionCheckInterval = window.setInterval(() => {
      const currentPermission = this.getPermissionStatus();
      if (currentPermission !== lastPermission) {
        lastPermission = currentPermission;
        this.notifyPermissionChange(currentPermission);
      }
    }, 1000); // Check every second
  }

  /**
   * Stop monitoring permission changes
   */
  stopPermissionMonitoring(): void {
    if (this.permissionCheckInterval !== null) {
      clearInterval(this.permissionCheckInterval);
      this.permissionCheckInterval = null;
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stopPermissionMonitoring();
    this.permissionChangeListeners.clear();
  }
}

// Export singleton instance
export const notificationService = new NotificationService();

// Clean up on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    notificationService.destroy();
  });
}
